import {
  constants,
  getFromLocalStorage,
  setAccessTokenInLocalStorage,
  setInLocalStorage,
  setInSessionStorage,
  setRefreshTokenInLocalStorage,
  setUserInLocalStorage,
} from 'app/utils/localStorage';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

type User = {
  access_token?: string;
  user_id?: string;
  name?: string;
  image_url: string;
  refresh_token: string;
  site_admin: string;
};

type AuthContextType = {
  user?: User;
  update: (user: any) => void;
  login: (user: any) => void;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element | null => {
  const [user, setUser] = useState<AuthContextType['user']>(
    getFromLocalStorage(constants.user) as any
  );
  const queryClient = useQueryClient();
  const value = useMemo(
    () => ({
      user,
      update: (newUserData: any) => {
        setUser((old: any) =>
          newUserData?.id === old.user_id
            ? {
                ...newUserData,
                user_id: newUserData?.id,
                image_url: newUserData.image,
              }
            : old
        );

        setUserInLocalStorage({
          ...user,
          name: newUserData.name,
          image_url: newUserData?.image,
        } as any);
      },
      login: (userData: any) => {
        if (!!userData?.remember_me) {
          setInLocalStorage('user_id', userData.user_id);
          setAccessTokenInLocalStorage(userData.access_token);
          setRefreshTokenInLocalStorage(userData.refresh_token);
          setUserInLocalStorage(userData);
        } else {
          setInSessionStorage(constants?.SESSION_STORAGE_DATA, userData);
        }
        setUser(userData);
        queryClient.resetQueries('getAuthorizedModules');
        queryClient.resetQueries(['getSettings']);
      },
    }),

    [user]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw Error('useAuth should be used within <AuthProvider />');
  return context;
};
